import { Button, Checkbox, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid } from "@mui/material";
import { map } from "rxjs";
import { createListStore, getFilterDto, getSort } from "../../common/listStore";
import { renderListStore } from "../../common/listStoreUi";
import { renderClose } from "../../common/dialog";
import { createEntityStore } from "../../common/entityStore";
import { listStoreInFilter } from "../../common/listStoreInFilter";
import { useDefaultValues } from "../../common/defaultValues";
import { useCallback, useMemo, useState } from "react";
import { CustomerBasicInfo } from "./types";
import { propNumberField } from "../../common/entityStoreForm";
import { customerDialogTitle } from "./common";
import { downloadFromApiPost } from "../../common/download";

export const customerArButtonTitle = "Customer A/R"

export interface ArDto {
    type: "SHP" | "PAY" | "RET" | "DUN" | "WO" | "REF" | "GW";
    btoAmt: number;
    rdcAmt: number;
    stat: string;
    date: string;
    ordId: number;
    invcId: string;
}

const allTypes: ArDto["type"][] = ["SHP", "PAY", "RET", "DUN", "WO", "REF", "GW"];

export interface ArListDto {
    items: ArDto[];
    balance: number;
    paid: number;
}

export function ArDialog(props: {
    close: () => void;
    customer: CustomerBasicInfo;
}) {
    const customer = props.customer;
    const def = useDefaultValues().serverData;
    const [useArStore] = useState(() => createEntityStore<ArListDto>({
        path: "customers/customerArs", id: customer.id, data: { items: [], balance: 0, paid: 0 }
    }));
    const arStore = useArStore();
    const [showEur, setShowEur] = useState(false);
    const [useArListStore] = useState(() => {
        const useStore = createListStore({
            data: arStore.serverData$.pipe(map(p => ({ items: p.data.items || [], isFetching: p.isFetching }))),
            addIndexId: true, listProps: [
                { column: "type", label: "Type", width: 70 },
                { column: "date", label: "Date", width: 110, type: "date" },
                { column: "ordId", label: "Order", width: 70, align: "right" },
                { column: "invcId", label: "Invoice", width: 120 },
                { column: "btoAmt", label: "Amount", width: 100, type: "currency" },
                { column: "rdcAmt", label: "Reduced", width: 100, type: "currency" },
                { column: "stat", label: "Status", flex: 1 },
            ]
        });
        useStore.getState().setFilterData("type", { props: ["type"], operator: "In", value: ["SHP"] });
        useStore.getState().fetch({ updateFetchFilterData: true });
        return useStore;
    });
    const arList = useArListStore(p => ({
        dataFilters: p.dataFilters,
        setFilterData: p.setFilterData,
        fetch: p.fetch,
    }));
    const type = listStoreInFilter("type", ["type"], arList, allTypes);

    function renderTypeCheckbox(value: ArDto["type"]) {
        return <FormControlLabel control={<Checkbox checked={type.isChecked(value)} onChange={() => type.toggle(value)} />} label={value} />
    }

    function exportAr() {
        downloadFromApiPost(`customers/customerArs/${customer.id}/xlsx`, {
            eur: showEur,
            filter: getFilterDto(useArListStore.getState()),
            sort: getSort(useArListStore.getState()),
        });
    }

    const transformCurrency = useCallback(function transformCurrency(value: number | null | undefined) {
        return value == null ? undefined :
            def.showEurAr && showEur ? Math.round(value / def.euroRate * 100) / 100 :
                value;
    }, [def.showEurAr, def.euroRate, showEur]);

    const arListConfig = useMemo(() => {
        return {
            columnValueTransforms: {
                "btoAmt": transformCurrency,
                "rdcAmt": transformCurrency,
            },
        };
    }, [transformCurrency]);

    return <>
        <DialogTitle>{customerDialogTitle("A/R", customer)}</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container>
                <Grid item sx={{ flexGrow: 1 }}>
                    {renderTypeCheckbox("SHP")}
                    {renderTypeCheckbox("PAY")}
                    {renderTypeCheckbox("RET")}
                    {renderTypeCheckbox("DUN")}
                    {renderTypeCheckbox("WO")}
                    {renderTypeCheckbox("REF")}
                    {renderTypeCheckbox("GW")}
                    <FormControlLabel label="All" control={<Checkbox checked={type.areAllChecked} onChange={type.toggleAll} />} />
                </Grid>
                <Grid item>
                    {def.showEurAr && <FormControlLabel label="EUR" control={<Checkbox checked={showEur} onChange={p => {
                        setShowEur(p.target.checked);
                    }} />} />}
                </Grid>
            </Grid>
            {renderListStore(useArListStore, arListConfig)}
            <Grid container spacing={2} mt={1}>
                <Grid item xs={3}>
                    {propNumberField(useArStore)("balance", null, "Balance", { isCurrency: true, disabled: true, transformValue: transformCurrency, currency: showEur ? "EUR" : undefined })}
                </Grid>
                <Grid item xs={3}>
                    {propNumberField(useArStore)("paid", null, "Total paid", { isCurrency: true, disabled: true, transformValue: transformCurrency, currency: showEur ? "EUR" : undefined })}
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button onClick={exportAr}>Export</Button>
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </>;
}
